// サービス利用（portal.md「サービス利用」/ portal-api.md GET /contracts/{contractId}・PUT /admin/contracts/{contractId}/subscriptions/{subsystem}）。
// サブシステムごとのサービス利用（URL・有効/無効）。admin は閲覧のみ、追加・更新は system-admin だけ。

import { useEffect, useState } from "react";
import { api, messageFor, SUBSYSTEMS, type Subscription, type Subsystem, type ContractRows } from "../lib/api";
import { useContract } from "./ContractLayout";

export function SubscriptionsPage() {
  const { me, contractId } = useContract();
  const sysadmin = me.platformRole === "system-admin";
  const [rows, setRows] = useState<ContractRows | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = async () => {
    try { setRows(await api.contract(contractId)); } catch (e) { setError(messageFor(e)); }
  };
  useEffect(() => { void load(); }, [contractId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!rows) return <main>{error ? <p className="error">{error}</p> : <p className="muted">読み込み中…</p>}</main>;

  const save = async (s: Subsystem, body: Omit<Subscription, "subsystem">) => {
    setError(null); setNotice(null);
    try { await api.putSubscription(contractId, s, body); await load(); setNotice(`${s} のサービス利用を保存しました。`); } catch (e) { setError(messageFor(e)); }
  };

  return (
    <main>
      <h1>契約</h1>
      <p className="muted">
        {sysadmin
          ? "サービス利用を無効にすると、サブシステム側に効くのはレジストリの TTL 満了後です。サービス利用は削除できず、無効にして止めます。"
          : "この契約で使えるサービス（サービス利用）です。サービス利用の追加・変更はシステム管理者に依頼してください。"}
      </p>
      {error && <p className="error">{error}</p>}
      {notice && <p className="ok">{notice}</p>}
      {sysadmin ? SUBSYSTEMS.map((s) => {
        const c = rows.subscriptions.find((x) => x.subsystem === s);
        return <SubscriptionForm key={`${s}:${JSON.stringify(c ?? null)}`} subsystem={s} subscription={c} onSave={(body) => save(s, body)} />;
      }) : <SubscriptionTable subscriptions={rows.subscriptions} />}
    </main>
  );
}

/** admin 向けの閲覧専用の一覧。 */
function SubscriptionTable({ subscriptions }: { subscriptions: Subscription[] }) {
  return (
    <table>
      <thead><tr><th>サービス</th><th>URL</th><th>状態</th></tr></thead>
      <tbody>
        {subscriptions.length === 0 && <tr><td colSpan={3} className="muted">サービス利用はありません。</td></tr>}
        {subscriptions.map((c) => (
          <tr key={c.subsystem}>
            <td>{c.subsystem}</td>
            <td className="muted">{c.serviceUrl}</td>
            <td><span className="badge">{c.subscriptionActive ? "有効" : "無効"}</span></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SubscriptionForm({ subsystem, subscription, onSave }: {
  subsystem: Subsystem; subscription: Subscription | undefined; onSave: (body: Omit<Subscription, "subsystem">) => void;
}) {
  const [subscriptionActive, setActive] = useState(subscription?.subscriptionActive ?? true);
  const [serviceUrl, setServiceUrl] = useState(subscription?.serviceUrl ?? "");
  const [open, setOpen] = useState(!!subscription);
  if (!open) {
    return (
      <div className="card row">
        <strong>{subsystem}</strong><span className="muted">サービス利用なし</span><span className="spacer" />
        <button onClick={() => setOpen(true)}>サービス利用を追加</button>
      </div>
    );
  }
  return (
    <div className="card">
      <div className="row">
        <strong>{subsystem}</strong>
        <span className="badge">{!subscription ? "新規" : subscription.subscriptionActive ? "有効" : "無効"}</span>
      </div>
      <label>サービスの URL（https://）</label>
      <input type="text" value={serviceUrl} onChange={(e) => setServiceUrl(e.target.value)} placeholder="https://" />
      <div className="checks">
        <label><input type="checkbox" checked={subscriptionActive} onChange={(e) => setActive(e.target.checked)} />サービス利用を有効にする</label>
      </div>
      <p className="row">
        <button className="primary" disabled={!serviceUrl.trim()}
          onClick={() => onSave({ subscriptionActive, serviceUrl: serviceUrl.trim() })}>保存</button>
        {!subscription && <button onClick={() => setOpen(false)}>キャンセル</button>}
      </p>
    </div>
  );
}
